:host {
  display: block;
  width: 100%;
}

.nylas-specific-time-availability-picker {
  display: flex;
  margin: 1rem;
  flex-direction: column;
  border-radius: var(--nylas-border-radius-2x);
  border: 1px solid var(--nylas-base-200);

  .content {
    padding: 1.5rem;
    border-radius: var(--nylas-border-radius-2x);
    color: var(--nylas-base-900);
    font-size: 16px;
    font-family: var(--nylas-font-family);
    background: var(--nylas-base-25);
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @media (max-width: 768px) {
      padding: 1rem;
      gap: 1.25rem;
    }
  }

  .header {
    padding: 1rem;
    border-bottom: 1px solid var(--nylas-base-200);

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: var(--nylas-base-900);
      text-align: left;
    }

    p {
      margin: 0.25rem 0 0 0;
      font-size: 0.875rem;
      font-weight: 400;
      line-height: 20px;
      color: var(--nylas-base-600);
      text-align: left;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  .timezone-selector {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1.25rem;
    background: var(--nylas-base-0);
    border: 1px solid var(--nylas-base-200);
    border-radius: var(--nylas-border-radius);

    @media (max-width: 768px) {
      padding: 1rem;
    }

    .sub-header {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      line-height: 1.5;
      color: var(--nylas-base-900);
    }

    .timezone-description {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: var(--nylas-base-600);
    }

    select-dropdown {
      margin-top: 0.25rem;
    }

    .timezone-error {
      margin-top: 0.5rem;
    }
  }

  .specific-times-section {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
    background: var(--nylas-base-0);
    border: 1px solid var(--nylas-base-200);
    border-radius: var(--nylas-border-radius);

    .section-header {
      margin: 0 0 0.5rem 0;
      font-size: 0.9375rem;
      font-weight: 600;
      line-height: 1.5;
      color: var(--nylas-base-900);
    }

    .section-description {
      margin: 0 0 1rem 0;
      font-size: 0.8125rem;
      line-height: 1.5;
      color: var(--nylas-base-600);
    }
  }

  .specific-times {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem 1rem;
    text-align: center;

    .empty-icon {
      margin-bottom: 1.5rem;
      opacity: 0.4;
      color: var(--nylas-base-400);
    }

    .empty-title {
      margin: 0 0 0.5rem 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--nylas-base-700);
      line-height: 1.5;
    }

    .empty-subtitle {
      margin: 0;
      font-size: 0.875rem;
      color: var(--nylas-base-500);
      line-height: 1.5;
      max-width: 300px;
    }
  }

  .specific-time-entry {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 1rem;
    border: 1px solid var(--nylas-base-200);
    border-radius: var(--nylas-border-radius);
    background: var(--nylas-base-0);
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      border-color: var(--nylas-base-300);
    }

    &:focus-within {
      border-color: var(--nylas-primary);
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }

    .entry-content {
      display: grid;
      grid-template: 1fr auto / auto 1fr;
      gap: 0.75rem;
      align-items: center;

      @media (max-width: 768px) {
        grid-template: unset;
        grid-template-areas:
          'date'
          'time_or_unavailable'
          'actions';
        grid-template-columns: 1fr auto auto;
        gap: 0.75rem;
      }
    }

    .date-input-container {
      flex: 0 0 auto;

      @media (max-width: 768px) {
        grid-area: date;
      }

      .date-input {
        width: 100%;
      }

      nylas-date-component::part(ic__date) {
        border: 1px solid var(--nylas-base-200);
        height: 48px;
        display: flex;
        align-items: center;
        border-radius: var(--nylas-border-radius);
        transition:
          border-color 0.2s ease,
          box-shadow 0.2s ease;

        &:focus-within {
          border-color: var(--nylas-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
      }
    }

    .time-pickers {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex: 1;
      min-width: 0;

      @media (max-width: 768px) {
        grid-area: time_or_unavailable;
      }

      nylas-time-window-picker {
        flex: 1;
        min-width: 0;
      }

      .time-separator {
        font-size: 0.875rem;
        color: var(--nylas-base-600);
        font-weight: 500;
        flex-shrink: 0;
        line-height: 1;
        user-select: none;
      }
    }

    .unavailable-text {
      flex: 1;
      display: flex;
      align-items: center;
      padding: 0 1rem;
      border-radius: var(--nylas-border-radius);
      background: var(--nylas-base-50);
      border: 1px solid var(--nylas-base-200);
      min-height: 48px;

      @media (max-width: 768px) {
        grid-area: time_or_unavailable;
      }

      .unavailable-label {
        font-size: 0.875rem;
        color: var(--nylas-base-600);
        font-weight: 500;
      }
    }

    .action-buttons {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      grid-column: 2;
      justify-content: end;

      @media (max-width: 768px) {
        grid-area: actions;
        justify-self: end;
      }
    }

    .toggle-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 1px solid var(--nylas-base-200);
      border-radius: var(--nylas-border-radius);
      background: var(--nylas-base-0);
      color: var(--nylas-base-500);
      cursor: pointer;
      transition: all 0.2s ease;
      flex-shrink: 0;

      &:hover {
        background: var(--nylas-base-50);
        border-color: var(--nylas-primary);
        color: var(--nylas-primary);
      }

      &:focus {
        outline: none;
        border-color: var(--nylas-primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &:active {
        background: var(--nylas-base-100);
      }

      &.unavailable {
        background: var(--nylas-error-50);
        border-color: var(--nylas-error-200);
        color: var(--nylas-error);

        &:hover {
          background: var(--nylas-error-100);
          border-color: var(--nylas-error);
        }
      }
    }

    .remove-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      padding: 0;
      border: 1px solid var(--nylas-base-200);
      border-radius: var(--nylas-border-radius);
      background: var(--nylas-base-0);
      color: var(--nylas-base-400);
      cursor: pointer;
      transition: all 0.2s ease;
      flex-shrink: 0;

      &:hover {
        background: var(--nylas-error-50);
        border-color: var(--nylas-error-200);
        color: var(--nylas-error);
      }

      &:focus {
        outline: none;
        border-color: var(--nylas-primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      &:active {
        background: var(--nylas-error-100);
      }
    }

    .error-message {
      width: 100%;
      padding: 0.5rem 0.75rem;
      background: var(--nylas-error-50);
      color: var(--nylas-error);
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: var(--nylas-border-radius);
      border: 1px solid var(--nylas-error-200);
      margin-top: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .add-button-container {
    display: flex;
    justify-content: flex-start;
    margin-top: 0.5rem;

    button-component {
      width: 100%;

      @media (max-width: 768px) {
        font-size: 0.875rem;
      }
    }
  }
}

// Override button-component styles for the add button
::part(nstap__add-button) {
  border: 1px dashed var(--nylas-base-200);
  background: var(--nylas-base-0);
  color: var(--nylas-base-700);

  &:hover {
    border-color: var(--nylas-primary);
    background: var(--nylas-base-50);
    color: var(--nylas-primary);
  }
}

// Custom styling for time picker components
::part(nstap__time-picker-container) {
  flex: 1;
  min-width: 0;
}

::part(nstap__time-picker-input) {
  width: 100%;
  font-size: 0.875rem;
  min-height: 48px;
  display: flex;
  align-items: center;
  border-radius: var(--nylas-border-radius);
}

// Custom styling for date input component
::part(nstap__date-input) {
  width: 100%;
}

::part(nstap__date-field) {
  width: 100%;
  font-size: 0.875rem;
  border: 1px solid var(--nylas-base-200);
  border-radius: var(--nylas-border-radius);
  padding: 0.5rem 0.75rem;

  &:focus {
    border-color: var(--nylas-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
}

::part(nstap__date-wrapper) {
  margin: 0;
}

::part(nstap__date-label) {
  margin: 0;
  padding: 0;

  p {
    display: none; // Hide label since we don't need it
  }
}
